import { resolveText } from '../../i18n/resolveText';
import { i18nService } from '../../i18n/I18nService';
import { getHashParams } from '../../dsl/hashParams';
import { checkEmail, register, ApiError } from '../../api/authApi';
import { createFormField, EMAIL_RE } from '../formField';
function createField(label, type, value = '') {
    return createFormField('auth', label, type, value);
}
// Mirrors profile.ts's openCancelBlockedModal: overlay + centered card with a
// single "close" action, appended straight to document.body since authFlow.ts
// has no shared modal-host element.
function openPrivacyPolicyModal(step) {
    const overlay = document.createElement('div');
    overlay.className = 'profile__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'profile__modal';
    const title = document.createElement('h2');
    title.className = 'profile__section-title';
    title.textContent = resolveText(step.privacyPolicyTitle);
    const body = document.createElement('div');
    for (const paragraph of resolveText(step.privacyPolicyText).split('\n\n')) {
        const p = document.createElement('p');
        p.className = 'profile__modal-text';
        p.textContent = paragraph;
        body.appendChild(p);
    }
    const actions = document.createElement('div');
    actions.className = 'profile__modal-actions';
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'cta';
    closeButton.textContent = resolveText(step.privacyPolicyCloseLabel);
    closeButton.addEventListener('click', () => overlay.remove());
    actions.appendChild(closeButton);
    modal.append(title, body, actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}
function createPrivacyNotice(step) {
    const notice = document.createElement('p');
    notice.className = 'auth__privacy-notice';
    const link = document.createElement('a');
    link.href = '#';
    link.className = 'auth__privacy-link';
    link.textContent = resolveText(step.privacyNoticeLinkLabel);
    link.addEventListener('click', (event) => {
        event.preventDefault();
        openPrivacyPolicyModal(step);
    });
    notice.append(document.createTextNode(resolveText(step.privacyNoticeBefore)), link, document.createElement('br'), document.createTextNode(resolveText(step.privacyNoticeAfter)));
    return notice;
}
export function renderAuthFlow(block) {
    const rawPlaceId = getHashParams().get('place');
    const placeId = Number(rawPlaceId);
    const hasPlaceId = rawPlaceId !== null && Number.isInteger(placeId);
    const section = document.createElement('section');
    section.className = 'auth';
    const stepContainer = document.createElement('div');
    stepContainer.className = 'auth__step';
    section.appendChild(stepContainer);
    function showEmailStep() {
        stepContainer.innerHTML = '';
        const title = document.createElement('h2');
        title.className = 'section-title auth__title';
        title.textContent = resolveText(block.email.title);
        const description = document.createElement('p');
        description.className = 'auth__desc';
        description.textContent = resolveText(block.email.description);
        const form = document.createElement('form');
        form.className = 'auth__form';
        const input = document.createElement('input');
        input.type = 'email';
        input.className = 'auth__input';
        input.placeholder = resolveText(block.email.placeholder);
        input.required = true;
        input.autocomplete = 'email';
        const error = document.createElement('p');
        error.className = 'auth__error';
        error.hidden = true;
        const submit = document.createElement('button');
        submit.type = 'submit';
        submit.className = 'cta auth__submit';
        submit.textContent = resolveText(block.email.submitLabel);
        form.append(input, error, submit);
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            error.hidden = true;
            const email = input.value.trim();
            if (!EMAIL_RE.test(email)) {
                error.textContent = resolveText(block.email.invalidEmailError);
                error.hidden = false;
                return;
            }
            submit.disabled = true;
            submit.classList.add('is-loading');
            checkEmail(email, hasPlaceId ? placeId : undefined, i18nService.getLocale())
                .then((result) => {
                if (result.exists) {
                    showSentStep(result.maskedEmail ?? email);
                }
                else {
                    showRegisterStep(email);
                }
            })
                .catch((err) => {
                console.error('Failed to check email:', err);
                error.textContent = resolveText(block.email.genericError);
                error.hidden = false;
                submit.disabled = false;
                submit.classList.remove('is-loading');
            });
        });
        stepContainer.append(title, description, form, createPrivacyNotice(block.email));
    }
    function showSentStep(maskedEmail) {
        stepContainer.innerHTML = '';
        const title = document.createElement('h2');
        title.className = 'section-title auth__title';
        title.textContent = resolveText(block.sent.title);
        const description = document.createElement('p');
        description.className = 'auth__desc';
        description.textContent = resolveText(block.sent.description, { maskedEmail });
        stepContainer.append(title, description);
    }
    function showRegisterStep(email) {
        stepContainer.innerHTML = '';
        const title = document.createElement('h2');
        title.className = 'section-title auth__title';
        title.textContent = resolveText(block.register.title);
        const form = document.createElement('form');
        form.className = 'auth__form';
        const emailField = createField(resolveText(block.register.emailLabel), 'email', email);
        emailField.input.readOnly = true;
        const nameField = createField(resolveText(block.register.nameLabel), 'text');
        const surnameField = createField(resolveText(block.register.surnameLabel), 'text');
        const phoneField = createField(resolveText(block.register.phoneLabel), 'tel');
        const error = document.createElement('p');
        error.className = 'auth__error';
        error.hidden = true;
        const submit = document.createElement('button');
        submit.type = 'submit';
        submit.className = 'cta auth__submit';
        submit.textContent = resolveText(block.register.submitLabel);
        form.append(emailField.wrapper, nameField.wrapper, surnameField.wrapper, phoneField.wrapper, error, submit);
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            error.hidden = true;
            const name = nameField.input.value.trim();
            const surname = surnameField.input.value.trim();
            const phone = phoneField.input.value.trim();
            if (!name || !surname || !phone) {
                error.textContent = resolveText(block.register.genericError);
                error.hidden = false;
                return;
            }
            submit.disabled = true;
            submit.classList.add('is-loading');
            register({ email, name, surname, phone, placeId: hasPlaceId ? placeId : undefined, locale: i18nService.getLocale() })
                .then((result) => {
                showRegisteredStep(result.maskedEmail);
            })
                .catch((err) => {
                if (err instanceof ApiError && err.code === 'email_exists') {
                    // This email already has a verified account — resend a sign-in
                    // link instead of dead-ending on a generic error.
                    checkEmail(email, placeId)
                        .then((result) => showSentStep(result.maskedEmail ?? email))
                        .catch((checkErr) => {
                        console.error('Failed to send sign-in link for existing account:', checkErr);
                        error.textContent = resolveText(block.register.genericError);
                        error.hidden = false;
                        submit.disabled = false;
                        submit.classList.remove('is-loading');
                    });
                    return;
                }
                console.error('Failed to register:', err);
                error.textContent = resolveText(block.register.genericError);
                error.hidden = false;
                submit.disabled = false;
                submit.classList.remove('is-loading');
            });
        });
        stepContainer.append(title, form);
    }
    function showRegisteredStep(maskedEmail) {
        stepContainer.innerHTML = '';
        const title = document.createElement('h2');
        title.className = 'section-title auth__title';
        title.textContent = resolveText(block.registered.title);
        const description = document.createElement('p');
        description.className = 'auth__desc';
        description.textContent = resolveText(block.registered.description, { maskedEmail });
        stepContainer.append(title, description);
    }
    showEmailStep();
    return section;
}
